// authReducer.js
import { PP_ACTIONS } from "../actions/ppActions";

const initialState = {
  user: null,
  notifications: [],
  urls: null,
  expireAlert: null,
  alertSet: false,
  instantRonAccepted: false,

  // Private Provider specific state (for admin access to PP features)
  pp: {
    // Submissions state
    submissions: {
      data: [],
      loading: false,
      filters: {},
      activeSubmission: null,
    },

    // Workbench state
    workbench: {
      data: null,
      loading: false,
      pages: [],
    },

    // Inspections state
    inspections: {
      templates: [],
      permitInspections: {},
      loading: false,
    },

    // Finance state
    finance: {
      payouts: [],
      statements: [],
      summary: null,
      loading: false,
    },

    // Notifications state
    ppNotifications: {
      data: [],
      loading: false,
      unreadCount: 0,
    },

    // Settings state
    settings: {
      data: null,
      loading: false,
    },

    // Global error state
    error: null,
  },
};

const userSessionReducer = (state = initialState, action) => {
  switch (action.type) {
    case "UPDATE_CITY_CONNECTS":
      // Guard against null user state
      if (!state.user) {
        return state;
      }
      return {
        ...state,
        user: {
          ...state.user,
          city_connects: action.payload,
        },
      };
    case "UPDATE_SELECTED_COUNTIES":
      // Guard against null user state
      if (!state.user || !state.user.user) {
        return state;
      }
      return {
        ...state,
        user: {
          ...state.user,
          user: {
            ...state.user.user,
            selectedCountiesNOC: action.payload,
          },
        },
      };
    case "CHANGE_USER_SMS_CONSENT":
      // Guard against null user state
      if (!state.user || !state.user.user) {
        return state;
      }
      return {
        ...state,
        user: {
          ...state.user,
          user: {
            ...state.user.user,
            smsConsent: action.payload,
          },
        },
      };
    case "UPDATE_ANALYTICS":
      // Guard against null user state
      if (!state.user || !state.user.user) {
        return state;
      }
      return {
        ...state,
        user: {
          ...state.user,
          user: {
            ...state.user.user,
            analytics: {
              ...state.user.user.analytics,
              ...action.payload, // Merge only the new entries from the payload
            },
          },
        },
      };
    case "UPDATE_EXTRA_COUNTY_DATA":
      // Guard against null user state
      if (!state.user || !state.user.tier) {
        return state;
      }
      return {
        ...state,
        user: {
          ...state.user,
          tier: {
            ...state.user.tier,
            additions: {
              ...state.user.tier.additions,
              county: action.payload,
            },
          },
        },
      };
    case "SET_INSTANT_RON_ACCEPTED":
      return { ...state, instantRonAccepted: true };
    case "SET_INSTANT_RON_INITIAL_STATE":
      return { ...state, instantRonAccepted: false };
    case "SET_INSTANT_RON_STOP":
      return { ...state, instantRonAccepted: "stop" };
    case "SET_INSTANT_RON_SUCCESS":
      return { ...state, instantRonAccepted: "success" };
    case "ADD_CONTRACTOR":
      return {
        ...state,
        user: {
          ...state.user,
          user: {
            ...state.user.user,
            contractors: [...state.user.user.contractors, action.payload],
          },
        },
      };
    case "UPDATE_CONTRACTOR":
      return {
        ...state,
        user: {
          ...state.user,
          user: {
            ...state.user.user,
            contractors: state.user.user.contractors.map((contractor) =>
              contractor.id === action.payload.id
                ? { ...contractor, ...action.payload }
                : contractor
            ),
          },
        },
      };
    case "USER_FIRST_LOGIN_OFF":
      return {
        ...state,
        user: {
          ...state.user,
          user: {
            ...state.user.user,
            firstLogin: false,
          },
        },
      };
    case "SET_WILL_USER_EXPIRE_ALERT":
      const isSetAlert = !action.payload ? false : true;
      return {
        ...state,
        expireAlert: action.payload,
        alertSet: isSetAlert,
      };
    case "PUSH_NEW_DESIGN_PRO": {
      return {
        ...state,
        user: {
          ...state.user,
          design_professionals: [
            ...state.user.design_professionals,
            action.payload,
          ],
        },
      };
    }
    case "ADD_PERMIT_PACKAGE_SIGNED_URL": {
      const { task_id, document } = action.payload;

      // Find the selected form and update the documents within that form
      const updatedNocUserForms = state.user.nocUserForms.map((form) => {
        if (form.task_id === task_id) {
          return {
            ...form,
            building_package: {
              ...form.building_package,
              selected_documents: document,
            },
          };
        }
        return form;
      });

      return {
        ...state,
        user: {
          ...state.user,
          nocUserForms: updatedNocUserForms,
        },
      };
    }
    case "UPDATE_DESIGN_PRO": {
      return {
        ...state,
        user: {
          ...state.user,
          design_professionals: state.user.design_professionals.map((pro) =>
            pro.id === action.payload.id ? { ...pro, ...action.payload } : pro
          ),
        },
      };
    }
    case "REMOVE_DESIGN_PRO": {
      return {
        ...state,
        user: {
          ...state.user,
          design_professionals: state.user.design_professionals.filter(
            (pro) => pro.id !== action.payload
          ),
        },
      };
    }
    case "UPDATE_NOC_FORMS": {
      return {
        ...state,
        user: {
          ...state.user,
          nocUserForms: action.payload,
        },
      };
    }
    case "UPDATE_NOTARY_USER_FORMS": {
      const new_form = action.payload;

      return {
        ...state,
        user: {
          ...state.user,
          notary_forms: new_form,
        },
      };
    }
    case "UPDATE_PAYMENT_CARDS": {
      const new_cards = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          payment_cards: new_cards,
        },
      };
    }
    case "UPDATE_NOC_TASKS": {
      const new_tasks = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          nocUserTasks: new_tasks,
        },
      };
    }
    case "UPDATE_SIGNEE_EMAIL": {
      const { source, updatedList } = action.payload;

      return {
        ...state,
        user: {
          ...state.user,
          notary_forms: {
            ...state.user.notary_forms,
            [source]: updatedList,
          },
        },
      };
    }
    case "ADD_USER_FULL_DESIGN_PROFESSIONALS":
      return {
        ...state,
        user: {
          ...state.user,
          design_professionals: action.payload,
          design_professionals_locked: false,
        },
      };
    case "UPDATE_URLS":
      return {
        ...state,
        urls: action.payload,
      };
    case "ADD_URL":
      return {
        ...state,
        urls: [...state.urls, action.payload],
      };
    case "UPDATE_USER":
      return {
        ...state,
        user: action.payload,
      };

    case "ADD_URL_TO_CREATED_FORM":
      return {
        ...state,
        user: {
          ...state.user,
          nocUserForms: action.payload,
        },
      };

    case "GET_NOTIFICATION":
      return {
        ...state,
        notifications: action.payload,
      };

    case "REFRESH_DISCOUNT_DATA":
      return {
        ...state,
        user: {
          ...state.user,
          tier: {
            ...state.user.tier,
            discount: action.payload,
          },
        },
      };
    case "UPDATE_DISCOUNT_DATA":
      const discount_type = action.payload.discount_type;
      const discount = action.payload.data;
      return {
        ...state,
        user: {
          ...state.user,
          tier: {
            ...state.user.tier,
            discount: {
              ...state.user.tier.discount,
              [discount_type]: discount,
            },
          },
        },
      };

    // Private Provider Actions (for admin access to PP features)
    case PP_ACTIONS.SET_SUBMISSIONS_LOADING:
      return {
        ...state,
        pp: {
          ...state.pp,
          submissions: {
            ...state.pp.submissions,
            loading: action.payload,
          },
        },
      };

    case PP_ACTIONS.SET_SUBMISSIONS:
      return {
        ...state,
        pp: {
          ...state.pp,
          submissions: {
            ...state.pp.submissions,
            data: action.payload,
          },
        },
      };

    case PP_ACTIONS.ADD_SUBMISSION:
      return {
        ...state,
        pp: {
          ...state.pp,
          submissions: {
            ...state.pp.submissions,
            data: [action.payload, ...state.pp.submissions.data],
          },
        },
      };

    case PP_ACTIONS.UPDATE_SUBMISSION:
      return {
        ...state,
        pp: {
          ...state.pp,
          submissions: {
            ...state.pp.submissions,
            data: state.pp.submissions.data.map((submission) =>
              submission.id === action.payload.id ? action.payload : submission
            ),
          },
        },
      };

    case PP_ACTIONS.SET_ACTIVE_SUBMISSION:
      return {
        ...state,
        pp: {
          ...state.pp,
          submissions: {
            ...state.pp.submissions,
            activeSubmission: action.payload,
          },
        },
      };

    case PP_ACTIONS.SET_SUBMISSION_FILTERS:
      return {
        ...state,
        pp: {
          ...state.pp,
          submissions: {
            ...state.pp.submissions,
            filters: action.payload,
          },
        },
      };

    // Workbench Actions
    case PP_ACTIONS.SET_WORKBENCH_LOADING:
      return {
        ...state,
        pp: {
          ...state.pp,
          workbench: {
            ...state.pp.workbench,
            loading: action.payload,
          },
        },
      };

    case PP_ACTIONS.SET_WORKBENCH_DATA:
      return {
        ...state,
        pp: {
          ...state.pp,
          workbench: {
            ...state.pp.workbench,
            data: action.payload.submission,
            pages: action.payload.pages || [],
          },
        },
      };

    case PP_ACTIONS.UPDATE_PAGE_STATUS:
      return {
        ...state,
        pp: {
          ...state.pp,
          workbench: {
            ...state.pp.workbench,
            pages: state.pp.workbench.pages.map((page) =>
              page.id === action.payload.pageId
                ? { ...page, ...action.payload }
                : page
            ),
          },
        },
      };

    // Inspections Actions
    case PP_ACTIONS.SET_INSPECTIONS_LOADING:
      return {
        ...state,
        pp: {
          ...state.pp,
          inspections: {
            ...state.pp.inspections,
            loading: action.payload,
          },
        },
      };

    case PP_ACTIONS.SET_INSPECTION_TEMPLATES:
      return {
        ...state,
        pp: {
          ...state.pp,
          inspections: {
            ...state.pp.inspections,
            templates: action.payload,
          },
        },
      };

    case PP_ACTIONS.ADD_INSPECTION_TEMPLATE:
      return {
        ...state,
        pp: {
          ...state.pp,
          inspections: {
            ...state.pp.inspections,
            templates: [...state.pp.inspections.templates, action.payload],
          },
        },
      };

    case PP_ACTIONS.UPDATE_INSPECTION_TEMPLATE:
      return {
        ...state,
        pp: {
          ...state.pp,
          inspections: {
            ...state.pp.inspections,
            templates: state.pp.inspections.templates.map((template) =>
              template.id === action.payload.id ? action.payload : template
            ),
          },
        },
      };

    case PP_ACTIONS.SET_PERMIT_INSPECTIONS:
      return {
        ...state,
        pp: {
          ...state.pp,
          inspections: {
            ...state.pp.inspections,
            permitInspections: {
              ...state.pp.inspections.permitInspections,
              [action.payload.permitId]: action.payload.inspections,
            },
          },
        },
      };

    case PP_ACTIONS.UPDATE_INSPECTION:
      const { permitId } = action.payload;
      const currentInspections =
        state.pp.inspections.permitInspections[permitId] || [];
      return {
        ...state,
        pp: {
          ...state.pp,
          inspections: {
            ...state.pp.inspections,
            permitInspections: {
              ...state.pp.inspections.permitInspections,
              [permitId]: currentInspections.map((inspection) =>
                inspection.id === action.payload.inspectionId
                  ? action.payload
                  : inspection
              ),
            },
          },
        },
      };

    // Finance Actions
    case PP_ACTIONS.SET_FINANCE_LOADING:
      return {
        ...state,
        pp: {
          ...state.pp,
          finance: {
            ...state.pp.finance,
            loading: action.payload,
          },
        },
      };

    case PP_ACTIONS.SET_PAYOUTS:
      return {
        ...state,
        pp: {
          ...state.pp,
          finance: {
            ...state.pp.finance,
            payouts: action.payload,
          },
        },
      };

    case PP_ACTIONS.UPDATE_PAYOUT:
      return {
        ...state,
        pp: {
          ...state.pp,
          finance: {
            ...state.pp.finance,
            payouts: state.pp.finance.payouts.map((payout) =>
              payout.id === action.payload.id ? action.payload : payout
            ),
          },
        },
      };

    case PP_ACTIONS.SET_STATEMENTS:
      return {
        ...state,
        pp: {
          ...state.pp,
          finance: {
            ...state.pp.finance,
            statements: action.payload,
          },
        },
      };

    case PP_ACTIONS.SET_FINANCE_SUMMARY:
      return {
        ...state,
        pp: {
          ...state.pp,
          finance: {
            ...state.pp.finance,
            summary: action.payload,
          },
        },
      };

    // Notifications Actions
    case PP_ACTIONS.SET_NOTIFICATIONS_LOADING:
      return {
        ...state,
        pp: {
          ...state.pp,
          ppNotifications: {
            ...state.pp.ppNotifications,
            loading: action.payload,
          },
        },
      };

    case PP_ACTIONS.SET_NOTIFICATIONS:
      return {
        ...state,
        pp: {
          ...state.pp,
          ppNotifications: {
            ...state.pp.ppNotifications,
            data: action.payload,
            unreadCount: action.payload.filter((n) => !n.read).length,
          },
        },
      };

    case PP_ACTIONS.UPDATE_NOTIFICATION:
      return {
        ...state,
        pp: {
          ...state.pp,
          ppNotifications: {
            ...state.pp.ppNotifications,
            data: state.pp.ppNotifications.data.map((notification) =>
              notification.id === action.payload.id
                ? action.payload
                : notification
            ),
            unreadCount: state.pp.ppNotifications.data
              .map((n) => (n.id === action.payload.id ? action.payload : n))
              .filter((n) => !n.read).length,
          },
        },
      };

    // Settings Actions
    case PP_ACTIONS.SET_SETTINGS_LOADING:
      return {
        ...state,
        pp: {
          ...state.pp,
          settings: {
            ...state.pp.settings,
            loading: action.payload,
          },
        },
      };

    case PP_ACTIONS.SET_PP_SETTINGS:
      return {
        ...state,
        pp: {
          ...state.pp,
          settings: {
            ...state.pp.settings,
            data: action.payload,
          },
        },
      };

    // Error Actions
    case PP_ACTIONS.SET_ERROR:
      return {
        ...state,
        pp: {
          ...state.pp,
          error: action.payload,
        },
      };

    case PP_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        pp: {
          ...state.pp,
          error: null,
        },
      };

    default:
      return state;
  }
};

export default userSessionReducer;
